<template>
  <span class="platform-help-nav">
    <button
      type="button"
      class="platform-help-launcher ace_sec"
      @click="toggleHelpPanel"
      :aria-expanded="showHelpPanel ? 'true' : 'false'"
      :aria-busy="isBootstrapping ? 'true' : 'false'"
      aria-controls="platform-help-panel"
      :disabled="isBootstrapping"
      title="Help"
    >
      <i
        v-if="isBootstrapping"
        class="fas fa-spinner fa-spin platform-help-launcher-icon"
        aria-hidden="true"
      ></i>
      <i
        v-else
        class="far fa-question-circle platform-help-launcher-icon"
        aria-hidden="true"
      ></i>
      <span class="platform-help-launcher-label">Help</span>
    </button>

    <!-- Mount widget off-screen while auth/chat UI loads; reveal popup only when ready -->
    <div
      v-if="sessionActive"
      id="platform-help-panel"
      :class="panelHostClasses"
      :role="showHelpPanel ? 'dialog' : undefined"
      :aria-label="showHelpPanel ? 'Platform Help' : undefined"
      :aria-hidden="showHelpPanel ? 'false' : 'true'"
    >
      <button
        v-if="showHelpPanel"
        type="button"
        class="platform-help-popup-close"
        @click="closeHelpPanel"
        aria-label="Close Help"
      >
        &times;
      </button>

      <div class="platform-help-popup-body">
        <pragya-widget
          v-if="canMountWidget"
          id="pragya-help-widget"
          ref="helpWidget"
          data-help-panel="true"
          embed-mode
          bot-purpose="help"
          position-type="relative"
          context="help"
          :user-id="userId"
          :institution-id="institutionMongoId"
          :tenant="tenantOpenId || institutionMongoId"
          :role="userRole"
        />
      </div>
    </div>
  </span>
</template>

<script>
export default {
  name: 'PlatformHelpPanel',
  props: {
    institution: {
      type: Object,
      default: null,
    },
    userRole: {
      type: String,
      default: null,
    },
    userId: {
      type: String,
      default: null,
    },
  },
  data () {
    return {
      sessionActive: false,
      showHelpPanel: false,
      isBootstrapping: false,
      popupWidthExpanded: false,
    }
  },
  computed: {
    panelHostClasses () {
      if (!this.showHelpPanel) {
        return ['platform-help-preload-host']
      }
      return [
        'platform-help-popup',
        { 'platform-help-popup--wide': this.popupWidthExpanded },
      ]
    },
    tenantOpenId () {
      if (this.institution && this.institution.openId) {
        return String(this.institution.openId)
      }
      return null
    },
    institutionMongoId () {
      if (this.institution && this.institution.uuid) {
        return String(this.institution.uuid)
      }
      const fromStorage = localStorage.getItem('institutionId')
      return fromStorage ? String(fromStorage) : null
    },
    canMountWidget () {
      return Boolean(this.userId && (this.tenantOpenId || this.institutionMongoId))
    },
  },
  watch: {
    sessionActive (active) {
      if (active) {
        this.$nextTick(() => {
          this.syncHelpWidgetProps()
          this.attachWidgetListeners()
        })
      } else {
        this.detachWidgetListeners()
      }
    },
    institution () {
      this.syncHelpWidgetProps()
    },
    userRole () {
      this.syncHelpWidgetProps()
    },
    userId () {
      this.syncHelpWidgetProps()
    },
  },
  beforeDestroy () {
    this.detachWidgetListeners()
  },
  methods: {
    toggleHelpPanel () {
      if (this.showHelpPanel) {
        this.closeHelpPanel()
        return
      }
      this.openHelpPanel()
    },
    openHelpPanel () {
      if (!this.canMountWidget || this.sessionActive) return
      this.isBootstrapping = true
      this.sessionActive = true
      this.showHelpPanel = false
    },
    closeHelpPanel () {
      this.showHelpPanel = false
      this.isBootstrapping = false
      this.popupWidthExpanded = false
      this.sessionActive = false
      this.detachWidgetListeners()
    },
    onWidgetReady () {
      if (!this.sessionActive) return
      this.isBootstrapping = false
      this.showHelpPanel = true
      this.syncHelpWidgetProps()
    },
    onWidgetDismiss () {
      this.closeHelpPanel()
    },
    onWidgetError () {
      this.closeHelpPanel()
    },
    onWidgetWidthChange (event) {
      const detail = event && event.detail
      this.popupWidthExpanded = detail && detail.expanded === true
    },
    attachWidgetListeners () {
      const widget = this.$refs.helpWidget || document.getElementById('pragya-help-widget')
      if (!widget || widget._platformHelpListenersAttached) return
      widget.addEventListener('pragya-widget-ready', this.onWidgetReady)
      widget.addEventListener('pragya-widget-dismiss', this.onWidgetDismiss)
      widget.addEventListener('pragya-widget-error', this.onWidgetError)
      widget.addEventListener('pragya-widget-width-change', this.onWidgetWidthChange)
      widget._platformHelpListenersAttached = true
    },
    detachWidgetListeners () {
      const widget = this.$refs.helpWidget || document.getElementById('pragya-help-widget')
      if (!widget) return
      widget.removeEventListener('pragya-widget-ready', this.onWidgetReady)
      widget.removeEventListener('pragya-widget-dismiss', this.onWidgetDismiss)
      widget.removeEventListener('pragya-widget-error', this.onWidgetError)
      widget.removeEventListener('pragya-widget-width-change', this.onWidgetWidthChange)
      widget._platformHelpListenersAttached = false
    },
    syncHelpWidgetProps () {
      const widget =
        this.$refs.helpWidget ||
        document.getElementById('pragya-help-widget')
      if (!widget || !this.userId) return

      widget.internalProps = {
        tenant: this.tenantOpenId || this.institutionMongoId,
        institutionId: this.institutionMongoId,
        role: this.userRole,
        userId: this.userId,
        context: 'help',
        botPurpose: 'help',
        embedMode: true,
      }
      this.attachWidgetListeners()
    },
  },
}
</script>

<style scoped>
/* Inline in navbar — do not float (breaks flex row and pushes ask advisor down) */
.platform-help-nav {
  display: inline-flex;
  align-items: center;
  position: relative;
  vertical-align: middle;
  margin: 0;
  z-index: 100;
}

.platform-help-launcher {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 36px;
  min-width: 88px;
  padding: 0 14px 0 10px;
  margin: 0;
  border: none;
  border-radius: 9999px;
  background: var(--TU__BtnClrPri, #157099);
  color: #fff;
  font-family: inherit;
  font-size: 15px;
  font-weight: 600;
  line-height: 1;
  cursor: pointer;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.18);
}

.platform-help-launcher:hover,
.platform-help-launcher:focus {
  background: var(--TU__BtnHoverClrPri, #125f84);
  color: #fff;
  outline: none;
}

.platform-help-launcher:disabled {
  opacity: 0.85;
  cursor: wait;
}

.platform-help-launcher-icon {
  font-size: 18px;
  color: #fff;
  line-height: 1;
}

.platform-help-launcher-label {
  color: #fff;
  font-size: 15px;
  font-weight: 600;
  letter-spacing: 0.01em;
}

.platform-help-preload-host {
  position: fixed;
  left: -9999px;
  top: 0;
  width: 400px;
  height: 560px;
  overflow: hidden;
  visibility: hidden;
  pointer-events: none;
}

.platform-help-popup {
  position: fixed;
  top: 54px;
  right: 40px;
  width: 400px;
  max-width: calc(100vw - 24px);
  height: 560px;
  max-height: calc(100vh - 72px);
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.18);
  z-index: 990;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transition: width 0.25s ease, height 0.25s ease;
}

.platform-help-popup--wide {
  width: 600px;
  height: min(704px, calc(100vh - 72px));
}

.platform-help-popup-close {
  position: absolute;
  top: 8px;
  right: 10px;
  z-index: 2;
  background: rgba(255, 255, 255, 0.92);
  border: none;
  border-radius: 9999px;
  width: 28px;
  height: 28px;
  font-size: 20px;
  line-height: 1;
  cursor: pointer;
  color: #374151;
}

.platform-help-popup-body {
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.platform-help-popup-body pragya-widget {
  display: block;
  width: 100%;
  height: 100%;
}

</style>



// WEBPACK FOOTER //
// src/app/navigation/components/PlatformHelpPanel.vue